// GanttNewStages.jsx
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Sparkles, X, ArrowUpDown, GripVertical, LockKeyhole, Check, File } from 'lucide-react';

export const GanttNewStages = ({
  allStagesList = [],
  procesory = [],
  canDragOnGant,
  moveable = true,
  isAnyResizing = false,
  onDragStart, // (stage, e) => void  <- dokładnie jak stableDragStart w GanttChartAll
  onDragEnd, // (e) => void         <- dokładnie jak stableDragEnd w GanttChartAll
  onMarkAsRead, // (e, stage) => void <- dokładnie jak handleMarkAsRead w GanttChartAll
  onSelectStage,
  onSelectAndCenterStage,
  buttonClassName = '',
  openUpwards = false,
}) => {
  const [showList, setShowList] = useState(false);
  const [sortAsc, setSortAsc] = useState(true); // true = od najstarszej po stage.utworzono
  const [isDraggingFromList, setIsDraggingFromList] = useState(false);
  const containerRef = useRef(null);

  // Zamykanie listy po kliknięciu poza komponentem
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowList(false);
      }
    };
    if (showList) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showList]);

  // Te same reguły co w GanttStageBar + blokada kłódki
  const checkIsStageDraggable = (stage) => {
    if (!stage) return false;
    if (!moveable || isAnyResizing) return false;
    if (stage.zablokowany == 1) return false;
    if (typeof canDragOnGant === 'function' && !canDragOnGant(stage.proces_nazwa_id)) {
      return false;
    }
    return true;
  };

  // Filtrowanie (stage.nowa == 1) i sortowanie po stage.utworzono
  const newStagesDetails = useMemo(() => {
    if (!Array.isArray(allStagesList) || allStagesList.length === 0) return [];

    const filtered = allStagesList.filter((stage) => {
      if (!stage || stage.typ_grupy == 1 || stage.status == 4) return false;
      return stage.nowa == 1;
    });

    return filtered.sort((a, b) => {
      const timeA = a.utworzono ? new Date(a.utworzono).getTime() : Infinity;
      const timeB = b.utworzono ? new Date(b.utworzono).getTime() : Infinity;

      const validA = Number.isNaN(timeA) ? Infinity : timeA;
      const validB = Number.isNaN(timeB) ? Infinity : timeB;

      return sortAsc ? validA - validB : validB - validA;
    });
  }, [allStagesList, sortAsc]);

  const count = newStagesDetails.length;

  const handleToggleButtonClick = () => {
    if (count > 0) {
      setShowList((prev) => !prev);
    } else {
      setShowList(false);
    }
  };

  // Start przeciągania z listy podpięty pod usePreciseDragDropGant
  const handleRowDragStart = (stage, e, isDraggable) => {
    if (!isDraggable) {
      e.preventDefault();
      return;
    }

    // Zerujemy grabOffset dla usePreciseDragDropGant (useGrabOffset: true),
    // aby linia celownika na wykresie była idealnie pod kursorem myszy
    const currentTargetProxy = new Proxy(e.currentTarget, {
      get(ctTarget, ctProp) {
        if (ctProp === 'getBoundingClientRect') {
          return () => ({
            left: e.clientX,
            top: e.clientY,
            right: e.clientX,
            bottom: e.clientY,
            width: 0,
            height: 0,
          });
        }
        const val = ctTarget[ctProp];
        return typeof val === 'function' ? val.bind(ctTarget) : val;
      },
    });

    const eventForHook = new Proxy(e, {
      get(target, prop) {
        if (prop === 'currentTarget') return currentTargetProxy;
        const val = target[prop];
        return typeof val === 'function' ? val.bind(target) : val;
      },
    });

    if (typeof onDragStart === 'function') {
      onDragStart(stage, eventForHook);
    }

    // Ukrywamy okienko na czas lotu klocka (bez odmontowywania z DOM, żeby nie przerwać dragu)
    setTimeout(() => {
      setIsDraggingFromList(true);
    }, 0);
  };

  const handleRowDragEnd = (e) => {
    setIsDraggingFromList(false);
    if (typeof onDragEnd === 'function') {
      onDragEnd(e);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className={buttonClassName}
        onClick={handleToggleButtonClick}
        style={{
          backgroundColor: showList ? '#0284c7' : '#63636396',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          padding: '6px 6px',
        }}
        title="Pokaż nowe prace (stage.nowa = 1)"
      >
        <File size={20} style={{ marginRight: '0px' }} />

        {count > 0 && (
          <span
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#ffffff',
              background: showList ? 'transparent' : '#0284c7',
              padding: '1px 6px',
              borderRadius: '10px',
            }}
          >
            {count}
          </span>
        )}
      </button>

      {showList && count > 0 && (
        <div
          style={{
            position: 'absolute',
            top: openUpwards ? 'auto' : '100%',
            bottom: openUpwards ? '100%' : 'auto',
            right: 0,
            marginTop: openUpwards ? 0 : '6px',
            marginBottom: openUpwards ? '6px' : 0,
            width: '340px',
            maxHeight: '360px',
            backgroundColor: '#1f2937',
            color: '#ffffff',
            border: '1px solid #374151',
            borderRadius: '6px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            opacity: isDraggingFromList ? 0 : 1,
            pointerEvents: isDraggingFromList ? 'none' : 'auto',
            transition: 'opacity 0.12s ease',
          }}
        >
          {/* Nagłówek okienka */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 10px',
              backgroundColor: '#374151',
              borderBottom: '1px solid #4b5563',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <span>Nowe prace ({count})</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                onClick={() => setSortAsc((prev) => !prev)}
                title={
                  sortAsc
                    ? 'Posortowano od najstarszej (kliknij, aby odwrócić)'
                    : 'Posortowano od najnowszej (kliknij, aby odwrócić)'
                }
                style={{
                  background: '#4b5563',
                  border: 'none',
                  color: '#e5e7eb',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  fontSize: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <ArrowUpDown size={12} />
                {sortAsc ? 'Najstarsze' : 'Najnowsze'}
              </button>

              <button
                onClick={() => setShowList(false)}
                title="Zamknij listę"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Lista wierszy z nowymi pracami */}
          <div
            style={{
              overflowY: 'auto',
              padding: '6px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            {newStagesDetails.map((stage) => {
              const createdDateStr = stage.utworzono
                ? new Date(stage.utworzono).toLocaleString('pl-PL', {
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'Brak daty';

              const startDateStr = stage.start
                ? new Date(stage.start).toLocaleString('pl-PL', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '';

              const procName =
                procesory.find((p) => p.id == stage.procesor_id)?.nazwa ||
                `Procesor ${stage.procesor_id}`;

              const isDraggable = checkIsStageDraggable(stage);

              return (
                <div
                  key={stage.global_id || stage.id}
                  draggable={isDraggable}
                  onDragStart={(e) => handleRowDragStart(stage, e, isDraggable)}
                  onDragEnd={handleRowDragEnd}
                  onClick={() => {
                    onSelectStage?.(stage);
                  }}
                  onDoubleClick={() => {
                    onSelectStage?.(stage);
                    onSelectAndCenterStage?.(stage);
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#374151')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  title={
                    isDraggable
                      ? 'Przeciągnij na wykres lub kliknij dwukrotnie, aby wycentrować'
                      : stage.zablokowany == 1
                      ? 'Etap zablokowany (kłódka)'
                      : !moveable
                      ? 'Przenoszenie klocków jest wyłączone na górnym pasku'
                      : 'Brak uprawnień do przesuwania tego procesu'
                  }
                  style={{
                    padding: '6px 8px',
                    borderRadius: '4px',
                    cursor: isDraggable ? 'grab' : 'not-allowed',
                    fontSize: '11px',
                    borderLeft: `3px solid ${isDraggable ? '#38bdf8' : '#6b7280'}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'background 0.15s',
                    opacity: isDraggable ? 1 : 0.75,
                    userSelect: 'none',
                  }}
                >
                  {/* Ikona uchwytu lub pomarańczowej kłódki jak na kafelku */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: stage.zablokowany == 1 ? 'orange' : isDraggable ? '#9ca3af' : '#6b7280',
                      flexShrink: 0,
                    }}
                  >
                    {stage.zablokowany == 1 ? (
                      <LockKeyhole size={14} />
                    ) : (
                      <GripVertical size={14} />
                    )}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      overflow: 'hidden',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '6px',
                      }}
                    >
                      <strong style={{ color: '#38bdf8' }}>
                        {stage.nr}_{String(stage.rok || '').substring(2, 4)} {stage.firma_nazwa}
                      </strong>

                      {onMarkAsRead ? (
                        <button
                          onClick={(e) => onMarkAsRead(e, stage)}
                          title="Oznacz jako przeczytane (zdejmij kropkę Nowa)"
                          style={{
                            backgroundColor: '#075985',
                            color: '#bae6fd',
                            border: '1px solid #0284c7',
                            fontSize: '9px',
                            fontWeight: 700,
                            padding: '1px 5px',
                            borderRadius: '3px',
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                          }}
                        >
                          <Check size={10} />
                          NOWA
                        </button>
                      ) : (
                        <span
                          style={{
                            backgroundColor: '#075985',
                            color: '#bae6fd',
                            fontSize: '9px',
                            fontWeight: 700,
                            padding: '1px 4px',
                            borderRadius: '3px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          NOWA
                        </span>
                      )}
                    </div>

                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {stage.tytul || stage.uwagi || 'Brak tytułu'}
                    </span>

                    <span style={{ color: '#9ca3af', fontSize: '10px' }}>
                      {procName}
                      {startDateStr && (
                        <>
                          {' | Start: '}
                          <strong style={{ color: '#e5e7eb' }}>{startDateStr}</strong>
                        </>
                      )}
                    </span>

                    <span style={{ color: '#9ca3af', fontSize: '10px' }}>
                      Utworzono: <strong style={{ color: '#34d399' }}>{createdDateStr}</strong>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(GanttNewStages);